import React, { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchConsentForm } from "../../../store/slices/consentSlice";
import logo from "../../../assets/images/logo-samplee.png";

export default function ConsentPrint() {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { currentConsentForm, isLoading, error } = useSelector(
    (state) => state.consent
  );

  useEffect(() => {
    if (id) dispatch(fetchConsentForm(id));
  }, [dispatch, id]);

  useEffect(() => {
    if (currentConsentForm) {
      const t = setTimeout(() => window.print(), 500);
      window.onafterprint = () => navigate(-1);
      return () => {
        clearTimeout(t);
        window.onafterprint = null;
      };
    }
  }, [currentConsentForm, navigate]);

  if (isLoading) return <div className="p-4 text-center">Loading…</div>;
  if (error) return <div className="p-4 text-danger text-center">{error}</div>;
  if (!currentConsentForm) return null;

  const d = currentConsentForm;
  const hasFinanceInfo = d.amount || d.payment_status || d.reference_no || d.payment_date;

  const fmt = (dateStr) =>
    dateStr
      ? new Date(dateStr).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
      : "";

  return (
    <>
      {/* ───────── PRINT STYLES ───────── */}
      <style jsx global>{`
        * {
          box-sizing: border-box !important;
          font-family: Arial, sans-serif !important;
        }
        body { background: #fff !important; color: #000 !important; margin: 0 !important; padding: 0 !important; }

        @page {
          size: auto;
          margin: 10mm !important;
        }

        .print-container { padding: 10px !important; position: relative !important; }

        .master-print-table {
          width: 100% !important;
          border-collapse: collapse !important;
          border: none !important;
        }
        .master-print-table > tbody > tr > td,
        .master-print-table > tfoot > tr > td {
          border: none !important;
          padding: 0 !important;
        }

        .print-footer {
          text-align: center !important;
          font-size: 0.85rem !important;
          color: #555 !important;
          border-top: 1px solid #ccc !important;
          padding-top: 8px !important;
          padding-bottom: 8px !important;
          margin-top: 15px !important;
        }

        .logo {
          position: absolute !important;
          top: 10px !important;
          left: 10px !important;
        }
        .logo img { height: 60px !important; }

        .header h3 {
          text-align: center !important;
          font-size: 1.5em !important;
          font-weight: bold !important;
          border-bottom: 1.5px solid #383838 !important;
          padding-bottom: 15px !important;
          margin-bottom: 10px !important;
        }

        .header h6 {
          text-align: center !important;
          font-size: 1.2em !important;
          margin-bottom: 5px !important;
        }

        .info-row {
          display: flex !important;
          justify-content: space-between !important;
          margin-bottom: 12px !important;
          font-size: 1rem !important;
        }

        .underline {
          border-bottom: 1px solid #000 !important;
          display: inline-block !important;
          min-width: 180px !important;
          margin-left: 8px !important;
          padding: 0 4px !important;
          font-weight: bold !important;
        }

        .section {
          margin-top: 22px !important;
        }

        .section-label {
          font-weight: bold !important;
          margin-bottom: 6px !important;
          display: block !important;
        }

        .text-box {
          border: 1px solid #000 !important;
          padding: 10px !important;
          min-height: 150px !important;
          font-size: 0.95rem !important;
          line-height: 1.6 !important;
        }
      `}</style>

      {/* LOGO */}
      <div className="logo">
        <img src={logo} alt="The Recovery House" />
      </div>

      <div className="print-container">
        <table className="master-print-table">
          <tbody>
            <tr>
              <td>
                <div className="header">
                  <h3>THE RECOVERY HOUSE</h3>
                  <h6>CONSENT FORM</h6>
                </div>

                {/* ───────── INFO ROWS (8 ITEMS) ───────── */}
                <div style={{ marginTop: "20px" }}>
                  <div className="info-row">
                    <div>
                      Client Name:
                      <span className="underline">{d.client_name || ""}</span>
                    </div>
                    <div>
                      MR No:
                      <span className="underline">{d.mr_no || ""}</span>
                    </div>
                  </div>

                  <div className="info-row">
                    <div>
                      Guardian Name:
                      <span className="underline">{d.guardian_name || ""}</span>
                    </div>
                    <div>
                      Relation with Client:
                      <span className="underline">{d.relation_to_client || ""}</span>
                    </div>
                  </div>

                  <div className="info-row">
                    <div>
                      Contact:
                      <span className="underline">{d.contact_no || ""}</span>
                    </div>
                    <div>
                      Date:
                      <span className="underline">{fmt(d.date || d.consent_date)}</span>
                    </div>
                  </div>

                  <div className="info-row">
                    <div>
                      Address:
                      <span className="underline" style={{ minWidth: "280px" }}>
                        {d.address || ""}
                      </span>
                    </div>
                    <div>
                      Signature:
                      <span className="underline" style={{ minWidth: "200px" }}></span>
                    </div>
                  </div>
                </div>

                {/* ───────── CONSENT TEXT ───────── */}
                <div className="section">
                  <span className="section-label">Consent (English):</span>
                  <div className="text-box text-start">
                    <ol className="ps-4">
                      <li>I allow the patient to be admitted and treated according to their current psychiatric and medical condition.</li>
                      <li>I confirm that all pre-existing physical or mental conditions have been truthfully disclosed.</li>
                      <li>I allow the clinical team to assess, diagnose, and treat the patient as required.</li>
                      <li>I allow prescribed medications and understand benefits and side effects.</li>
                      <li>The treatment plan may change at the psychiatrist’s discretion.</li>
                      <li>If the patient becomes harmful or aggressive, approved safety techniques may be used.</li>
                      <li>In emergencies, the facility may transfer the patient if required.</li>
                      <li>Treatment duration depends entirely on the patient’s condition.</li>
                      <li>Doctor meetings depend on availability and schedule.</li>
                      <li>Clinical information will only be shared within the treatment team.</li>
                      <li>I will follow all facility rules.</li>
                      <li>The facility is not responsible for issues due to undisclosed conditions.</li>
                      <li>I take responsibility for unsafe patient behavior.</li>
                      <li>I confirm everything has been explained and I give voluntary consent.</li>
                      <li>I have read and agree to the family handbook.</li>
                    </ol>
                  </div>
                </div>

                <div className="section">
                  <span className="section-label">رضامندی (اردو):</span>
                  <div className="text-box text-end" dir="rtl">
                    <ol className="pe-4">
                      <li>میں مریض کے علاج اور داخلے کی اجازت دیتا/دیتی ہوں۔</li>
                      <li>میں تصدیق کرتا/کرتی ہوں کہ تمام جسمانی و ذہنی معلومات درست فراہم کی گئی ہیں۔</li>
                      <li>میں کلینیکل ٹیم کو تشخیص اور علاج کی اجازت دیتا/دیتی ہوں۔</li>
                      <li>مجھے ادویات کے فوائد اور ممکنہ نقصانات سمجھا دیے گئے ہیں۔</li>
                      <li>علاج میں تبدیلی ڈاکٹر کی صوابدید پر ہو سکتی ہے۔</li>
                      <li>اگر مریض نقصان دہ ہو تو حفاظتی طریقے استعمال کیے جا سکتے ہیں۔</li>
                      <li>ہنگامی حالت میں مریض کو منتقل کیا جا سکتا ہے۔</li>
                      <li>علاج کا دورانیہ مریض کی حالت پر منحصر ہوگا۔</li>
                      <li>ڈاکٹر سے ملاقات دستیابی کے مطابق ہوگی۔</li>
                      <li>مریض کی معلومات صرف علاج کے مقصد کے لیے استعمال ہوں گی۔</li>
                      <li>میں ادارے کے تمام قواعد و ضوابط پر عمل کروں گا/گی۔</li>
                      <li>غلط یا نامکمل معلومات کی ذمہ داری مجھ پر ہوگی۔</li>
                      <li>مریض کے غیر محفوظ رویے کی ذمہ داری میری ہوگی۔</li>
                      <li>مجھے تمام تفصیلات سمجھا دی گئی ہیں اور میں رضامندی دیتا/دیتی ہوں۔</li>
                    </ol>
                  </div>
                </div>

                {/* {hasFinanceInfo && (
                  <div className="section" style={{ marginTop: '20px' }}>
                    <span className="section-label">Finance Information</span>
                    <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '10px' }}>
                      <tbody>
                        <tr>
                          <td style={{ border: '1px solid #000', padding: '8px', background: '#f0f0f0', width: '20%' }}>Amount</td>
                          <td style={{ border: '1px solid #000', padding: '8px' }}><strong>{d.amount || " "}</strong></td>
                          <td style={{ border: '1px solid #000', padding: '8px', background: '#f0f0f0', width: '20%' }}>Payment Status</td>
                          <td style={{ border: '1px solid #000', padding: '8px' }}><strong>{d.payment_status || " "}</strong></td>
                        </tr>
                        <tr>
                          <td style={{ border: '1px solid #000', padding: '8px', background: '#f0f0f0' }}>Reference No</td>
                          <td style={{ border: '1px solid #000', padding: '8px' }}><strong>{d.reference_no || " "}</strong></td>
                          <td style={{ border: '1px solid #000', padding: '8px', background: '#f0f0f0' }}>Payment Date</td>
                          <td style={{ border: '1px solid #000', padding: '8px' }}><strong>{d.payment_date ? fmt(d.payment_date) : " "}</strong></td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                )} */}
              </td>
            </tr>
          </tbody>
          <tfoot>
            <tr>
              <td>
                <div className="print-footer">
                  This is a system generated print no signature required, <strong>NOT VALID FOR COURT</strong>
                </div>
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </>
  );
}
